@import "../../../globals/scss/input";

$container-padding-vertical: 11px; // results in 48px ($interaction-element-height)
$space-before-container-text: $space-before-text + $container-padding-horizontal;
$border-width: 1px;
$margin-inline: $baseline-4 - 5px; // -5px fine adjustment

.component {
  position: relative; // For hidden input positioning

  &:not(.has-cta) {
    cursor: pointer;
  }

  &.disabled {
    cursor: not-allowed;
  }
}

:host {
  display: inline-block; // "inline-*" to prevent clickable area width of 100%
  max-width: 100%; // IE11: make sure content does break and not overflow (e.g. "description")

  .component {
    --box-shadow-color: transparent;
    display: flex;
    flex-flow: column; // display validation-message below

    &.indicator-less {
      ::slotted(sdx-tag) {
        position: absolute;
        width: 100%;
        top: 0;
        transform: translateY(-50%);
        justify-content: center;
      }

      input {
        @include focus-visible("&.focus-visible+label", false) {
          border-radius: $border-radius-large;
        }

        &:checked {
          @include focus-visible("&.focus-visible+label", false) {
            outline-offset: -5px;
          }
        }

        &:disabled {
          + label {
            color: unset;
          }
        }
      }
    }

    input {
      &:disabled {
        + label {
          ::slotted([slot="description"]) {
            color: $color-input-item-disabled;
          }
        }
      }

      &.hide-checked-icon {
        + label {
          padding-left: 0;

          &::before,
          &::after {
            display: none;
          }
        }
      }
    }

    label {
      ::slotted([slot="description"]) {
        @include font-semi-light;
        @include text-small;
        display: block;
        letter-spacing: 0;
        padding-top: 5px;
        padding-bottom: 3px; // together with padding-top, fits into 8px-grid
        color: $color-checkbox-supporting;
      }
    }
  }
}

// Safari 14 CSS specificity fix:
// :host(.class) selector needs to be *below* the general :host selector
:host(.input-container) {
  display: block; // act like a <div>

  .component {
    &.inline {
      height: 100%;
    }

    &:hover {
      input:not(:disabled) {
        &.focus,
        &:checked {
          + label {
            --box-shadow-color: #{$token-border-color-interaction-hover};
            border-color: $token-border-color-interaction-hover;
          }
        }

        &.focus:not(:checked) {
          + label {
            --box-shadow-color: #{$color-input-normal-form-hover};
            border-color: $color-input-normal-form-hover;
          }
        }

        &:checked {
          + label {
            .icon {
              color: $token-text-color-interaction-hover;
            }
          }
        }

        + label {
          --box-shadow-color: #{$color-input-normal-form-hover};
          border-color: $color-input-normal-form-hover;

          .icon {
            color: $color-input-normal-form-hover;
          }
        }
      }
    }

    input {
      &:not(:checked) {
        @include focus-visible("&.focus-visible+label", false);

        &.focus:not(.focus-visible) {
          // do not color the border in case outline overlaps it
          + label {
            --box-shadow-color: transparent;
            border-color: $token-border-color-interaction-default;
          }
        }
      }

      &:checked {
        // When there's no icon emphasizing "selected", color the label instead
        &.hide-checked-icon {
          + label {
            color: $token-text-color-interaction-default;
          }
        }

        + label {
          --box-shadow-color: #{$token-border-color-interaction-default};
          border-color: $token-border-color-interaction-default;

          .icon {
            color: $token-text-color-interaction-default;
          }
        }
      }

      &:disabled {
        &:checked {
          + label {
            --box-shadow-color: #{$color-input-item-disabled};
          }
        }

        + label {
          border-color: $color-input-item-disabled;
          color: $color-input-item-disabled;

          .icon {
            color: $color-input-item-disabled;
          }
        }
      }

      &.hide-checked-icon {
        // used in color-picker, option-picker

        @include focus-visible("&.focus-visible+label") {
          outline-offset: -5px;
        }

        + label {
          padding-left: $container-padding-horizontal;
        }
      }
    }

    label {
      box-sizing: border-box;
      border: $border-width solid $color-checkbox-supporting;
      box-shadow: inset 0 0 0 $border-width var(--box-shadow-color);
      border-radius: $border-radius-medium;
      padding: $container-padding-vertical $container-padding-horizontal
        $container-padding-vertical $space-before-container-text;
      transition:
        border-color 200ms $standard-easing,
        box-shadow 200ms $standard-easing,
        color 200ms $standard-easing;

      &::before,
      &::after {
        margin: $container-padding-vertical $container-padding-vertical + 1px;
      }

      .icon-placeholder {
        margin-top: $baseline - 2px; // 2px fine adjustment
        visibility: hidden;
      }

      .top-section {
        display: flex;
        justify-content: space-between;

        .top-section-content {
          flex-grow: 1;
        }
      }

      .image {
        vertical-align: bottom;
        width: calc(100% + $space-before-text);
        margin-top: $container-padding-vertical;
        margin-left: -$space-before-text;
        margin-bottom: $baseline - $container-padding-vertical;
        border-radius: $border-radius-medium;
      }

      .icon {
        position: absolute;
        bottom: $container-padding-horizontal;
        left: 0;
        right: 0;
        text-align: center;
        transition: color 200ms $standard-easing;
        color: $color-checkbox-empty;
      }
    }
  }
}

:host(.sdx--dark-theme) {
  .component {
    &.indicator-less {
      input {
        @include focus-visible--dark-theme("&.focus-visible+label", false);

        &:checked {
          @include focus-visible--dark-theme("&.focus-visible+label", false) {
            outline-offset: -5px;
          }
        }
      }
    }

    input {
      &:disabled {
        + label {
          ::slotted([slot="description"]) {
            color: $color-disabled-dark-theme;
          }
        }
      }

      + label {
        ::slotted([slot="description"]) {
          color: $color-int-gray-dark-theme;
        }
      }
    }
  }
}

:host(.input-container.sdx--dark-theme) {
  .component {
    &:hover {
      input:not(:disabled) {
        &.focus,
        &:checked {
          + label {
            --box-shadow-color: #{$color-int-blue2-dark-theme--active};
            border-color: $color-int-blue2-dark-theme--active;
          }
        }

        &:checked {
          + label {
            .icon {
              color: $color-int-blue2-dark-theme--active;
            }
          }
        }

        + label {
          --box-shadow-color: #{$color-int-gray-dark-theme--active};
          border-color: $color-int-gray-dark-theme--active;

          .icon {
            color: $color-int-gray-dark-theme--active;
          }
        }
      }
    }

    input {
      @include focus-visible--dark-theme("&.focus-visible+label");

      &:not(:checked) {
        &.focus:not(.focus-visible) {
          + label {
            border-color: $color-int-blue2-dark-theme;
          }
        }
      }

      &:checked {
        &.hide-checked-icon {
          + label {
            color: $color-int-blue2-dark-theme;
          }
        }

        + label {
          --box-shadow-color: #{$color-int-blue2-dark-theme};
          border-color: $color-int-blue2-dark-theme;

          .icon {
            color: $color-int-blue2-dark-theme;
          }
        }
      }

      &:disabled {
        + label {
          --box-shadow-color: #{$color-disabled-dark-theme};
          border-color: $color-disabled-dark-theme;
          color: $color-disabled-dark-theme;

          .icon {
            color: $color-disabled-dark-theme;
          }
        }
      }
    }

    label {
      border-color: $color-int-gray-dark-theme--active;

      .icon {
        color: $color-int-gray-dark-theme;
      }
    }
  }
}
